
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { fetchCustomSchemaDefinition } from '@/composables/custom-data/customData';
import type { ActionPayload, ApiReferencePayload } from './types';

interface FieldDef {
  name: string;
  type: string;
}

interface Token {
  text: string;
  cls: string;
}

function getExampleValue(type: string): string {
  switch (type) {
    case 'number':
    case 'integer':
    case 'decimal':
    case 'float':
      return '123';
    case 'bool':
    case 'boolean':
      return 'true';
    case 'date':
      return '"2024-01-15"';
    case 'datetime':
      return '"2024-01-15T10:30:00Z"';
    case 'json':
      return '{}';
    case 'geolocation':
      return '"48.8566,2.3522"';
    default:
      return '"example"';
  }
}

function tokenize(curl: string): Token[] {
  const tokens: Token[] = [];
  const raw = curl;
  let i = 0;

  while (i < raw.length) {
    if (raw.substring(i).startsWith('curl')) {
      tokens.push({ text: 'curl', cls: 'tk-cmd' });
      i += 4;
      continue;
    }

    if (raw[i] === '-' && i > 0 && raw[i - 1] === ' ' && /[XHd]/.test(raw[i + 1] || '')) {
      const flag = raw.substring(i, i + 2);
      tokens.push({ text: flag, cls: 'tk-flag' });
      i += 2;
      continue;
    }

    const methodMatch = raw.substring(i).match(/^(GET|POST|PATCH|PUT|DELETE)\b/);
    if (methodMatch) {
      tokens.push({ text: methodMatch[1], cls: 'tk-method' });
      i += methodMatch[1].length;
      continue;
    }

    if (raw[i] === "'") {
      let end = raw.indexOf("'", i + 1);
      if (end === -1) end = raw.length - 1;
      const content = raw.substring(i, end + 1);
      if (content.includes('http')) {
        tokens.push({ text: content, cls: 'tk-url' });
      } else if (content.includes('{') || content.includes('[')) {
        tokens.push({ text: content, cls: 'tk-json' });
      } else {
        tokens.push({ text: content, cls: 'tk-string' });
      }
      i = end + 1;
      continue;
    }

    if (raw[i] === '\\' && raw[i + 1] === '\n') {
      tokens.push({ text: ' \\\n', cls: 'tk-plain' });
      i += 2;
      continue;
    }

    tokens.push({ text: raw[i], cls: 'tk-plain' });
    i += 1;
  }

  return tokens;
}

export default defineComponent({
  name: 'ApiReferenceModal',

  components: {
    Dialog,
    SpmButton,
  },

  setup(_, { expose }) {
    const { t } = useI18n();

    const isVisible = ref(false);
    const schemaName = ref('');
    const definitionId = ref<number>(0);
    const fields = ref<FieldDef[]>([]);
    const copiedKey = ref<string | null>(null);
    const expandedKey = ref<string | null>(null);

    const baseUrl = computed(() => {
      const clientApiUrl = process.env.VUE_APP_NESTJS_CLIENT_API_URL || '';
      const version = process.env.VUE_APP_NESTJS_CLIENT_API_VERSION || 'v1';
      return `${clientApiUrl}/${version}`;
    });

    const modalTitle = computed(() => `${schemaName.value} — ${t('myShopData.apiReference.title')}`);

    const toggle = (key: string) => {
      expandedKey.value = expandedKey.value === key ? null : key;
    };

    const buildJsonBody = (fieldList: FieldDef[], indent: number) => {
      const pad = ' '.repeat(indent);
      const innerPad = ' '.repeat(indent + 2);
      if (fieldList.length === 0) {
        return `${pad}{\n${innerPad}"field1": "value1"\n${pad}}`;
      }
      const lines = fieldList.map((f) => `${innerPad}"${f.name}": ${getExampleValue(f.type)}`);
      return `${pad}{\n${lines.join(',\n')}\n${pad}}`;
    };

    const endpoints = computed(() => {
      const id = definitionId.value || '{DEFINITION_ID}';
      const url = baseUrl.value;

      const postBody = buildJsonBody(fields.value, 4);
      const patchFields = fields.value.length > 0 ? fields.value.slice(0, 2) : [{ name: 'field1', type: 'text' }];
      const patchBody = buildJsonBody(patchFields, 2);

      const curlPost = `curl -X POST '${url}/custom-data-records/${id}' \\
  -H 'Content-Type: application/json' \\
  -H 'spm-api-key: YOUR_API_KEY' \\
  -d '{
  "records": [
${postBody}
  ]
}'`;

      const curlGet = `curl -X GET '${url}/custom-data-records/${id}?limit=10&offset=0' \\
  -H 'spm-api-key: YOUR_API_KEY'`;

      const curlPatch = `curl -X PATCH '${url}/custom-data-records/${id}/1' \\
  -H 'Content-Type: application/json' \\
  -H 'spm-api-key: YOUR_API_KEY' \\
  -d '{
${patchBody.trimStart()}
}'`;

      const curlDelete = `curl -X DELETE '${url}/custom-data-records/${id}?id=1' \\
  -H 'spm-api-key: YOUR_API_KEY'`;

      const curlBulkDelete = `curl -X POST '${url}/custom-data-records/${id}/bulk-delete' \\
  -H 'Content-Type: application/json' \\
  -H 'spm-api-key: YOUR_API_KEY' \\
  -d '{
  "ids": [1, 2, 3]
}'`;

      return [
        {
          key: 'post',
          method: 'POST',
          route: `/custom-data-records/${id}`,
          title: t('myShopData.apiReference.createOrUpdate'),
          description: t('myShopData.apiReference.createOrUpdateDesc'),
          queryParams: false,
          curl: curlPost,
          tokens: tokenize(curlPost),
        },
        {
          key: 'get',
          method: 'GET',
          route: `/custom-data-records/${id}`,
          title: t('myShopData.apiReference.listRecords'),
          description: t('myShopData.apiReference.listRecordsDesc'),
          queryParams: true,
          curl: curlGet,
          tokens: tokenize(curlGet),
        },
        {
          key: 'patch',
          method: 'PATCH',
          route: `/custom-data-records/${id}/{recordId}`,
          title: t('myShopData.apiReference.updateRecord'),
          description: t('myShopData.apiReference.updateRecordDesc'),
          queryParams: false,
          curl: curlPatch,
          tokens: tokenize(curlPatch),
        },
        {
          key: 'delete',
          method: 'DELETE',
          route: `/custom-data-records/${id}?id={recordId}`,
          title: t('myShopData.apiReference.deleteRecord'),
          description: t('myShopData.apiReference.deleteRecordDesc'),
          queryParams: false,
          curl: curlDelete,
          tokens: tokenize(curlDelete),
        },
        {
          key: 'bulkDelete',
          method: 'POST',
          route: `/custom-data-records/${id}/bulk-delete`,
          title: t('myShopData.apiReference.bulkDelete'),
          description: t('myShopData.apiReference.bulkDeleteDesc'),
          queryParams: false,
          curl: curlBulkDelete,
          tokens: tokenize(curlBulkDelete),
        },
      ];
    });

    const copyToClipboard = async (text: string, key: string) => {
      try {
        await navigator.clipboard.writeText(text);
        copiedKey.value = key;
        setTimeout(() => {
          copiedKey.value = null;
        }, 2000);
      } catch {
        // Fallback silently
      }
    };

    const open = async (payload: ActionPayload | ApiReferencePayload) => {
      expandedKey.value = null;
      isVisible.value = true;

      // Mode "inline" : fields passés directement depuis l'éditeur (création ou édition)
      if ('schemaName' in payload) {
        schemaName.value = payload.schemaName;
        definitionId.value = payload.definitionId ?? 0;
        fields.value = (payload.fields || []).map((f: any) => ({
          name: f.name || 'unknown',
          type: f.type || 'text',
        }));
        return;
      }

      // Mode classique : depuis la liste des schemas (ActionPayload)
      schemaName.value = payload.schemaItem.schema_name;
      definitionId.value = payload.schemaItem.id_custom_object ?? 0;
      fields.value = [];

      if (definitionId.value) {
        const result = await fetchCustomSchemaDefinition(definitionId.value);
        if (result.success && result.data) {
          const def = result.data;
          if (Array.isArray(def.fields)) {
            fields.value = def.fields.map((f: any) => ({
              name: f.name || 'unknown',
              type: f.type || 'text',
            }));
          }
        }
      }
    };

    const close = () => {
      isVisible.value = false;
    };

    expose({ open });

    return {
      t,
      isVisible,
      definitionId,
      baseUrl,
      modalTitle,
      copiedKey,
      expandedKey,
      endpoints,
      toggle,
      copyToClipboard,
      close,
    };
  },
});
